import React, { useEffect, useState } from 'react';
import ApiInterface from '@/commons/api/ApiInterface';
import ReactDOM from 'react-dom';
import styles from './CloudImage.module.scss';
import CustomButton from "@/commons/components/buttons/customButton/CustomButton";

const imageCache: Map<string, string> = new Map();
const pendingRequests: Map<string, Promise<string>> = new Map();

type CloudImageProps = {
    uuid?: string;
    alt?: string;
    className?: string;
    style?: React.CSSProperties;
    placeholderSrc?: string;
    fallbackSrc?: string;
    enableFullscreen?: boolean;
};

const CloudImage: React.FC<CloudImageProps> = ({
                                                   uuid,
                                                   alt = '',
                                                   className = '',
                                                   style = {},
                                                   placeholderSrc = 'https://storage.eblcraft.it/static/images/loading.gif',
                                                   fallbackSrc = 'https://storage.eblcraft.it/static/images/image-unavailable.png',
                                                   enableFullscreen = false,
                                               }) => {
    const [src, setSrc] = useState<string | null>(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        if (!uuid) {
            setError(true);
            return;
        }

        // Se è una stringa base64, la mostriamo direttamente
        if (uuid.startsWith('data:image')) {
            setSrc(uuid);
            return;
        }

        let isMounted = true;

        const resolveImage = async () => {
            if (imageCache.has(uuid)) {
                setSrc(imageCache.get(uuid)!);
                return;
            }

            if (pendingRequests.has(uuid)) {
                try {
                    const result = await pendingRequests.get(uuid)!;
                    if (isMounted) setSrc(result);
                } catch {
                    if (isMounted) setError(true);
                }
                return;
            }

            const fetchPromise = ApiInterface.getFileUrl(uuid)
                .then((url) => {
                    if (url) {
                        imageCache.set(uuid, url);
                        return url;
                    } else {
                        throw new Error("No URL");
                    }
                })
                .finally(() => {
                    pendingRequests.delete(uuid);
                });

            pendingRequests.set(uuid, fetchPromise);

            try {
                const finalUrl = await fetchPromise;
                if (isMounted) setSrc(finalUrl);
            } catch {
                if (isMounted) setError(true);
            }
        };

        resolveImage();

        return () => {
            isMounted = false;
        };
    }, [uuid]);

    const openFullScreen = (imageSrc: string) => {
        const container = document.createElement("div");
        document.body.appendChild(container);

        const handleClose = () => {
            ReactDOM.unmountComponentAtNode(container);
            container.remove();
        };

        ReactDOM.render(<ZoomImageModal imageSrc={imageSrc} onClose={handleClose} />, container);
    };

    if (error || !src) {
        return (
            <img
                src={error ? fallbackSrc : placeholderSrc}
                alt={alt}
                className={className}
                style={style}
                width="35px"
            />
        );
    }

    return (
        <img
            src={src}
            loading="lazy"
            alt={alt}
            className={`${styles.lazy} ${className}`}
            style={style}
            onClick={enableFullscreen ? () => openFullScreen(src) : undefined}
            onError={() => setError(true)}
        />
    );
};

const ZoomImageModal: React.FC<{ imageSrc: string; onClose: () => void }> = ({ imageSrc, onClose }) => {
    const [zoom, setZoom] = useState(1);

    const zoomImage = () => setZoom((prev) => Math.round((prev + 0.2) * 10) / 10);
    const deZoomImage = () => setZoom((prev) => Math.max(0.2, Math.round((prev - 0.2) * 10) / 10));

    useEffect(() => {
        const zoomableImage = document.getElementById("zoomableImage") as HTMLImageElement;
        if (zoomableImage) {
            zoomableImage.style.transform = `scale(${zoom})`;
        }
    }, [zoom]);

    return (
        <div className={styles.postBigImageBackground} onClick={onClose}>
            <div className={styles.imageContainer} onClick={(e) => e.stopPropagation()}>
                <div className={styles.imageButtons}>
                    <CustomButton className={styles.imageZoom} onClick={zoomImage}>+</CustomButton>
                    Zoom: {zoom}
                    <CustomButton className={styles.imageZoom} onClick={deZoomImage}>-</CustomButton>
                </div>
                <div className={styles.imageBox}>
                    <img className={styles.zoomableImage} id="zoomableImage" src={imageSrc} alt="zoom" />
                </div>
            </div>
        </div>
    );
};

export default CloudImage;